import AppButton from '@nxsys/components/AppButton';
import AppInputSearch from '@nxsys/components/AppInputSearch';
import AppLoader from '@nxsys/components/AppLoader';
import AppSelect from '@nxsys/components/AppSelect';
import AppTagStatus from '@nxsys/components/AppTagStatus';
import { defaultTheme } from '@nxsys/constants/defaultConfig';
import { useEmployeeMessageSocket } from '@nxsys/hooks/useEmployeeMessageSocket';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import { AppDispatch, AppState } from '@nxsys/store';
import { employeeMessageActions } from '@nxsys/store/slices/employeeMessage/reducer/employeeMessageSlice';
import {
  Conversation,
  ConversationPriority,
  ConversationStatus,
} from '@nxsys/store/slices/message/types';
import { Icons } from '@nxsys/types/icons';
import { EmployeeRecordType } from '@nxsys/types/models/employee';
import { formatStatus } from '@nxsys/utils';
import { Button, Table, Tag, Typography } from 'antd';
import { ColumnsType } from 'antd/lib/table';
import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';
import { useDebounce } from '@nxsys/hooks/useDebounce';
import { IRELAND } from '@nxsys/store/constants/keys';
import CreateNewMessageModal from './components/CreateNewMessageModal';
import ViewMessageHistoryModal from './components/ViewMessageHistoryModal';

const statusOptions = [
  { label: 'All Status', value: '' },
  { label: 'New', value: 'NEW' },
  { label: 'WIP', value: 'WIP' },
  { label: 'Closed', value: 'CLOSED' },
];

const getIncludeParticipants = (isBureau: boolean, isAdmin: boolean) => {
  if (!isBureau && isAdmin) return ['BROADCASTED_ADMIN' as const];
  if (isBureau && isAdmin) return ['SELECTED_ADMIN' as const];
  if (isBureau && !isAdmin) return ['EMPLOYER' as const];
  return [];
};

const priorityLabels: Record<ConversationPriority, string> = {
  HIGH: 'High',
  MEDIUM: 'Medium',
  LOW: 'Low',
};

type Props = {
  employeeData?: EmployeeRecordType;
};

const MessageHistory: React.FC<Props> = ({ employeeData }) => {
  const { messages } = useTypedIntl();
  const dispatch = useDispatch<AppDispatch>();
  const {
    isAdmin,
    isBureau,
    payrollCountry,
    user: userFromStore,
  } = useSelector((state: AppState) => state.appStore);
  const user = userFromStore as any;
  const isIreland = payrollCountry === IRELAND;

  const { conversations, loading, nextCursor } = useSelector(
    (state: AppState) => state.employeeMessageStore,
  );

  const employeeUserId = employeeData?.userRef?.[0]?.id;
  const employeeName = employeeData
    ? `${employeeData.forename || ''} ${employeeData.surname || ''}`.trim()
    : '';

  const [activeStatus, setActiveStatus] = useState('');
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 500);

  // Socket for real-time messages
  useEmployeeMessageSocket({
    userId: user?.id,
    agencyId: user?.agencyId?.id,
    companyParentId: user?.companyParentId,
    isAdmin,
  });

  // Load conversations (initial or filter change)
  const loadData = useCallback(() => {
    if (!employeeUserId) return;
    dispatch(
      employeeMessageActions.eeListConversations({
        limit: 20,
        search: debouncedSearch || undefined,
        status: (activeStatus as ConversationStatus) || undefined,
        participantUserIds: [employeeUserId],
        includeParticipants: getIncludeParticipants(isBureau, isAdmin),
      }),
    );
  }, [dispatch, activeStatus, debouncedSearch, employeeUserId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Load more with cursor
  const handleLoadMore = () => {
    if (!employeeUserId || !nextCursor || loading) return;
    dispatch(
      employeeMessageActions.eeListConversations({
        limit: 20,
        cursor: nextCursor,
        search: debouncedSearch || undefined,
        status: (activeStatus as ConversationStatus) || undefined,
        participantUserIds: [employeeUserId],
        includeParticipants: getIncludeParticipants(isBureau, isAdmin),
      }),
    );
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      dispatch(employeeMessageActions.resetState());
    };
  }, [dispatch]);

  const handleStatusChange = (value: { label: string; value: any }) => {
    setActiveStatus(value?.value ?? '');
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(e.target.value);
  };

  // View modal
  const [openViewModal, setOpenViewModal] = useState(false);
  const [selectedConversation, setSelectedConversation] =
    useState<Conversation | null>(null);

  const toggleViewModal = (record?: Conversation) => {
    if (record) {
      setSelectedConversation(record);
      setOpenViewModal(true);
    } else {
      setOpenViewModal(false);
      setSelectedConversation(null);
    }
  };

  // Create modal
  const [openCreateModal, setOpenCreateModal] = useState(false);

  const columns: ColumnsType<Conversation> = [
    {
      key: 'actions',
      title: messages['common.action'],
      width: 100,
      fixed: 'left',
      align: 'left',
      render: (_, record) => (
        <ul className="flex items-center justify-start gap-x-2">
          <li>
            <div
              className="cursor-pointer"
              onClick={() => toggleViewModal(record)}
            >
              <Icons.IconDoubleChat />
            </div>
          </li>
        </ul>
      ),
    },
    {
      key: 'status',
      title: messages['common.status'],
      dataIndex: 'status',
      render: (value) => (
        <span className={`${formatStatus(value)}`}>
          <AppTagStatus className={`tag-popup ${formatStatus(value)}`}>
            {formatStatus(value)}
          </AppTagStatus>
        </span>
      ),
      width: 150,
    },
    {
      key: 'subject',
      title: messages['common.subject'],
      dataIndex: 'subject',
    },
    {
      key: 'priority',
      title: messages['common.priority'],
      dataIndex: 'priority',
      render: (value: ConversationPriority | null) =>
        value ? <Tag>{priorityLabels[value] || value}</Tag> : '-',
    },
    {
      key: 'dueDate',
      title: messages['common.dueDate'],
      dataIndex: 'dueDate',
      render: (value) =>
        value
          ? moment(value)
              .tz(isIreland ? 'Europe/Dublin' : 'Europe/London')
              .format('DD/MM/YYYY')
          : '-',
    },
    {
      key: 'description',
      title: messages['common.description'],
      dataIndex: 'description',
      render: (value) => <div className="truncate">{value}</div>,
    },
  ];

  // Guard: no userRef
  if (!employeeUserId) {
    return (
      <div className="flex items-center justify-center h-[300px] text-gray-400">
        {messages['common.messageHistory'] || 'Message History'} — This employee
        does not have a linked user account. Messaging is unavailable.
      </div>
    );
  }

  return (
    <div>
      <Typography.Title
        level={5}
        className="!mb-6 !text-[20px] !leading-[1.6] !tracking-[-0.02em] !text-[#1A1D1F]"
      >
        {messages['common.messageHistory']}
      </Typography.Title>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-x-2">
          <AppInputSearch onChange={handleSearchChange} />
          <AppSelect
            options={statusOptions}
            onChange={handleStatusChange}
            defaultValue={statusOptions[0]}
            placeholder={messages['common.status']}
            style={{ width: 150 }}
            allowClear={false}
          />
        </div>
        <AppButton
          name={messages['common.newMessage']}
          leftIcon={<Icons.IconPlus color={defaultTheme.theme.palette.white} />}
          onClick={() => setOpenCreateModal(true)}
        />
      </div>
      {loading && conversations.length === 0 && <AppLoader />}
      {conversations.length > 0 && (
        <>
          <Table
            rowKey="id"
            loading={false}
            dataSource={conversations}
            columns={columns}
            pagination={false}
            scroll={{ x: 1000, y: 700 }}
          />
          {nextCursor && (
            <div className="flex justify-center mt-4">
              <Button onClick={handleLoadMore} loading={loading}>
                {messages['common.loadMore'] || 'Load More'}
              </Button>
            </div>
          )}
        </>
      )}
      {!loading && conversations.length === 0 && (
        <div className="flex items-center justify-center h-[200px] text-gray-400">
          {messages['messageHistory.empty'] || 'No messages found'}
        </div>
      )}
      {selectedConversation && (
        <ViewMessageHistoryModal
          isOpen={openViewModal}
          toggle={() => toggleViewModal()}
          data={selectedConversation}
          onMessageSent={loadData}
        />
      )}
      <CreateNewMessageModal
        isOpen={openCreateModal}
        toggle={() => setOpenCreateModal(false)}
        employeeUserId={employeeUserId}
        employeeName={employeeName}
        onSuccess={loadData}
      />
    </div>
  );
};

export default MessageHistory;
